import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { Printer, ArrowLeft, Sparkle } from "@phosphor-icons/react";

export default function PrintRoster() {
  const [users, setUsers] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/users"),
      api.get("/class/info"),
    ])
      .then(([u, c]) => {
        setUsers(u.data.filter((x) => x.role === "student"));
        setClassInfo(c.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const loginUrl = (username) => {
    const origin = window.location.origin;
    return `${origin}/login?u=${encodeURIComponent(username)}`;
  };

  return (
    <div className="min-h-screen bg-[#FFFDF8] print:bg-white">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          .print-card {
            page-break-after: always;
            break-after: page;
            min-height: calc(100vh - 24mm);
          }
          .print-card:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
      `}</style>

      {/* Top toolbar — hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to="/admin"
            data-testid="print-back-link"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={16} weight="bold" /> Back to Admin
          </Link>
          <div className="text-sm text-slate-500" data-testid="print-count">
            {users.length} student card{users.length === 1 ? "" : "s"} ready
          </div>
          <button
            onClick={() => window.print()}
            disabled={loading || users.length === 0}
            data-testid="print-now-btn"
            className="btn-tactile-yellow inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-outfit font-bold text-sm hover:bg-yellow-500 disabled:opacity-50"
          >
            <Printer size={16} weight="bold" /> Print all
          </button>
        </div>
      </div>

      <div className="no-print max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="font-outfit text-3xl sm:text-4xl font-black tracking-tight">First-day handouts</h1>
        <p className="text-slate-600 mt-1">
          One page per student. Each handout has the student's name, a QR code
          that auto-fills their username on the sign-in page, and a space to
          write their password.
        </p>
        <div className="text-xs text-slate-500 mt-2">
          Tip: Use your browser's print dialog → "Save as PDF" if you want a
          digital copy.
        </div>
      </div>

      {loading ? (
        <div className="no-print max-w-5xl mx-auto px-4 sm:px-6 py-6 text-slate-500" data-testid="print-loading">
          Loading students...
        </div>
      ) : users.length === 0 ? (
        <div className="no-print max-w-5xl mx-auto px-4 sm:px-6 py-6 text-slate-500" data-testid="print-empty">
          No student accounts yet. Add some from the Admin panel first.
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 space-y-6 print:space-y-0 print:px-0 print:max-w-none">
          {users.map((u) => (
            <article
              key={u.id}
              data-testid={`print-card-${u.id}`}
              className="print-card bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-sm print:shadow-none print:border-2 print:border-slate-300 print:rounded-2xl flex flex-col"
            >
              {/* Header */}
              <header className="flex items-center gap-2 mb-6 pb-4 border-b border-dashed border-slate-200">
                <div className="w-10 h-10 rounded-2xl bg-yellow-400 flex items-center justify-center">
                  <Sparkle weight="fill" className="text-yellow-950" size={22} />
                </div>
                <div>
                  <div className="font-outfit font-black text-lg leading-none">Alpha AI Discovery Club</div>
                  <div className="text-xs text-slate-500">{classInfo?.class_name || "Class handout"}</div>
                </div>
                <div className="ml-auto text-xs text-slate-500 text-right">
                  <div className="font-bold tracking-wider uppercase">Class code</div>
                  <div className="font-mono text-sm text-slate-800">{classInfo?.class_code || "—"}</div>
                </div>
              </header>

              {/* Big greeting */}
              <div className="mb-8">
                <p className="text-slate-500 text-sm font-bold tracking-wider uppercase">Hello,</p>
                <h2 className="font-outfit text-5xl sm:text-6xl font-black tracking-tight leading-none break-words">
                  {u.name}
                </h2>
                <p className="font-caveat text-3xl text-yellow-700 mt-2">welcome to the club!</p>
              </div>

              {/* QR + creds */}
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 items-start mb-6">
                <div className="bg-white border-2 border-slate-200 rounded-2xl p-3 inline-block self-start">
                  <QRCodeSVG
                    value={loginUrl(u.username)}
                    size={180}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#0F172A"
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-1">
                      Step 1 — Scan the QR
                    </div>
                    <p className="text-sm text-slate-700">
                      Point your phone camera at the QR. It opens the sign-in
                      page with your username already filled in.
                    </p>
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-1">
                      Step 2 — Your username
                    </div>
                    <div className="font-mono text-lg bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 inline-block">
                      {u.username}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-1">
                      Step 3 — Your password
                    </div>
                    <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg px-3 py-3 text-slate-400 italic text-sm">
                      Ask your teacher and write it here.
                    </div>
                  </div>
                </div>
              </div>

              {/* URL fallback for kids without phones */}
              <div className="mt-auto pt-4 border-t border-dashed border-slate-200 text-xs text-slate-500">
                Or open this link on any device:
                <div className="font-mono text-slate-700 mt-1 break-all">
                  {loginUrl(u.username)}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
